import { getAutoCompleteSuggestions } from '@griply/utils';
import { useSortedSmartLists } from 'features/smart-lists';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  IconFormIcons,
  IconFormNames,
  Search,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuNoResults,
} from 'shared/components/ui/popup-menu';
import { useFocusChildrenWithArrows } from 'shared/hooks/use-focus-children-with-arrows';
import { ID } from 'shared/types/id';

import * as Styled from './select-smart-lists-context-menu.style';

export type SelectSmartListsContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'onOutside' | 'position' | 'children'
> & {
  onSmartList: (id: ID) => void;
  activeSmartLists?: ID[];
  onClose: () => void;
};

// Acceptable parallel of SelectTagsContextMenu (reuse-over-reinvent.md): same
// picker shape, but smart-lists render a dynamic icon with a fallback (vs a
// static tag glyph) and carry a nullable color — a different render contract.
export const SelectSmartListsContextMenu: React.FC<
  SelectSmartListsContextMenuProps
> = ({ onClose, onSmartList, activeSmartLists = [], ...popupMenuProps }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const smartLists = useSortedSmartLists();
  const [searchValue, setSearchValue] = useState('');

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const filteredSmartLists = useMemo(
    () => getAutoCompleteSuggestions(searchValue, smartLists, ['name']),
    [searchValue, smartLists],
  );

  useFocusChildrenWithArrows(containerRef);

  return (
    <PopupMenu
      {...popupMenuProps}
      ref={containerRef}
      onOutside={onClose}
      position="bottom-end"
      initialFocus={inputRef}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuInput
            ref={inputRef}
            startIcon={Search}
            value={searchValue}
            placeholder={t('general.labels.search')}
            onChange={onChangeInput}
          />
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList>
        {!filteredSmartLists.length && !!searchValue && (
          <PopupMenuNoResults>
            {t('general.labels.no-results')}
          </PopupMenuNoResults>
        )}
        {filteredSmartLists.map(({ id, name, color, icon }) => {
          const onSelect = () => onSmartList(id);

          return (
            <PopupMenuListItem key={id}>
              <PopupMenuButton
                start={
                  <Styled.IconContainer $color={color ?? undefined}>
                    <Icon
                      icon={IconFormIcons[icon || IconFormNames.SmartList]}
                    />
                  </Styled.IconContainer>
                }
                end={
                  activeSmartLists.includes(id) ? (
                    <Styled.CheckIconContainer>
                      <Icon icon={Check} />
                    </Styled.CheckIconContainer>
                  ) : undefined
                }
                onClick={onSelect}
              >
                {name}
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}
      </PopupMenuList>
    </PopupMenu>
  );
};
